import { logger } from '@meshwatch/logging';
import { DynamoDB } from 'aws-sdk/clients/all';
import { ClientConfiguration } from 'aws-sdk/clients/dynamodb';
import { AWSError } from 'aws-sdk/lib/error';
import { PromiseResult } from 'aws-sdk/lib/request';

import { dynamoClientConfiguration } from './configuration';

type OmitTableName<T> = Omit<T, 'TableName'>;

export class DynamoDBTableClient {
  private readonly tableName: string;
  private readonly dynamoDbClient: DynamoDBClient;

  public constructor(tableName: string, dynamoDbClient: DynamoDBClient) {
    this.tableName = tableName;
    this.dynamoDbClient = dynamoDbClient;
  }

  public createTable(
    params: OmitTableName<DynamoDB.Types.CreateTableInput>
  ): Promise<PromiseResult<DynamoDB.CreateTableOutput, AWSError>> {
    return this.dynamoDbClient.createTable({ ...params, TableName: this.tableName });
  }

  public putDocumentToDynamo(
    params: OmitTableName<DynamoDB.DocumentClient.PutItemInput>
  ): Promise<PromiseResult<DynamoDB.DocumentClient.PutItemOutput, AWSError>> {
    return this.dynamoDbClient.putDocumentToDynamo({ ...params, TableName: this.tableName });
  }

  public queryTable(
    params: OmitTableName<DynamoDB.DocumentClient.QueryInput>
  ): Promise<PromiseResult<DynamoDB.DocumentClient.QueryOutput, AWSError>> {
    return this.dynamoDbClient.queryTable({ ...params, TableName: this.tableName });
  }

  public scanTable(
    params: OmitTableName<DynamoDB.DocumentClient.ScanInput>
  ): Promise<PromiseResult<DynamoDB.DocumentClient.ScanOutput, AWSError>> {
    return this.dynamoDbClient.scanTable({ ...params, TableName: this.tableName });
  }

  public deleteItem(
    params: OmitTableName<DynamoDB.DocumentClient.DeleteItemInput>
  ): Promise<PromiseResult<DynamoDB.DocumentClient.DeleteItemOutput, AWSError>> {
    return this.dynamoDbClient.deleteItem({ ...params, TableName: this.tableName });
  }

  public updateItem(
    params: OmitTableName<DynamoDB.DocumentClient.UpdateItemInput>
  ): Promise<PromiseResult<DynamoDB.DocumentClient.UpdateItemOutput, AWSError>> {
    return this.dynamoDbClient.updateItem({ ...params, TableName: this.tableName });
  }
}

export class DynamoDBClient {
  private readonly dynamodb: DynamoDB;
  private readonly dynamodbDocumentClient: DynamoDB.DocumentClient;

  public constructor(config: ClientConfiguration) {
    const dynamoConfig = dynamoClientConfiguration(config);
    this.dynamodb = new DynamoDB(dynamoConfig);
    this.dynamodbDocumentClient = new DynamoDB.DocumentClient({
      ...dynamoConfig,
      convertEmptyValues: true,
    });
  }

  public createTable = (createTableParams: DynamoDB.Types.CreateTableInput) => {
    return this.dynamodb
      .createTable(createTableParams)
      .promise()
      .then(createTableResponse => {
        if ('development' === process.env.NODE_ENV) {
          logger.debug('[DynamoDB]: Created table', { createTableResponse });
        }
        return createTableResponse;
      })
      .catch(err => {
        logger.error('[DynamoDB]: Unable to create table', err, { createTableParams });
        throw err;
      });
  };

  public transactWriteItems = (
    transactWriteParams: DynamoDB.DocumentClient.TransactWriteItemsInput
  ) => {
    if ('development' === process.env.NODE_ENV) {
      logger.debug('[DynamoDb]: transactWriteParams', transactWriteParams);
    }

    return this.dynamodbDocumentClient
      .transactWrite(transactWriteParams)
      .promise()
      .catch(err => {
        logger.error(`[DynamoDB] transactWriteItems failed`, err, {
          transactWriteParams,
        });
        throw err;
      });
  };

  public putDocumentToDynamo = (putItemParams: DynamoDB.DocumentClient.PutItemInput) => {
    const { TableName } = putItemParams;
    if ('development' === process.env.NODE_ENV) {
      logger.debug(`[DynamoDb]: putting Item to table [${TableName}]`, { putItemParams });
    }

    return this.dynamodbDocumentClient
      .put(putItemParams)
      .promise()
      .catch(err => {
        logger.error(`[DynamoDB] put to table [${TableName}] failed`, err, { putItemParams });
        throw err;
      });
  };

  public queryTable = (queryTableParams: DynamoDB.DocumentClient.QueryInput) => {
    const { TableName } = queryTableParams;
    if ('development' === process.env.NODE_ENV) {
      logger.debug(`[DynamoDb]: querying table [${TableName}]`, { queryTableParams });
    }

    return this.dynamodbDocumentClient
      .query(queryTableParams)
      .promise()
      .catch(err => {
        logger.error(`[DynamoDb] query to table [${TableName}] failed`, err, { queryTableParams });
        throw err;
      });
  };

  public scanTable = (scanTableParams: DynamoDB.DocumentClient.ScanInput) => {
    const { TableName } = scanTableParams;
    if ('development' === process.env.NODE_ENV) {
      logger.debug(`[DynamoDb]: scanTable: ${TableName}`, scanTableParams);
    }

    return this.dynamodbDocumentClient
      .scan(scanTableParams)
      .promise()
      .catch(err => {
        logger.error(`[DynamoDb] scan to table [${TableName}] failed`, err, { scanTableParams });
        throw err;
      });
  };

  public deleteItem = (deleteItemParams: DynamoDB.DocumentClient.DeleteItemInput) => {
    const { TableName } = deleteItemParams;
    if ('development' === process.env.NODE_ENV) {
      logger.debug(`[DynamoDB]: deleting item from table [${TableName}]`, deleteItemParams);
    }

    return this.dynamodbDocumentClient
      .delete(deleteItemParams)
      .promise()
      .catch(err => {
        logger.error('[DynamoDB]: Unable to delete item', err, { deleteItemParams });
        throw err;
      });
  };

  public updateItem = (updateItemParams: DynamoDB.DocumentClient.UpdateItemInput) => {
    const { TableName } = updateItemParams;
    if ('development' === process.env.NODE_ENV) {
      logger.debug(`[DynamoDB]: updating item in table [${TableName}]`, updateItemParams);
    }

    return this.dynamodbDocumentClient
      .update(updateItemParams)
      .promise()
      .catch(err => {
        logger.error('[DynamoDB]: Unable to update item', err, { updateItemParams });
        throw err;
      });
  };
}

const defaultClientConfig: ClientConfiguration = dynamoClientConfiguration();

logger.debug('[DynamoDB]: Default client configuration', { defaultClientConfig });

export const DEFAULT_DYNAMO_DB_CLIENT = new DynamoDBClient(defaultClientConfig);
